import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
} from '@tanstack/react-table';
import {
  Calendar,
  MapPin,
  Clock,
  Stethoscope,
  Video,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import type { AppointmentWithRelations } from '@vagusx/clients';
import { appointmentDateParts } from '@/lib/format';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { AppointmentDetailSheet } from './AppointmentDetailSheet';
import { AppointmentEhrSyncStatus } from './AppointmentEhrSyncStatus';
import { syncingRefetchInterval } from '@/lib/ehr-sync-state';

// =============================================================================
// Types
// =============================================================================

export interface PatientAppointmentListProps {
  /** Patient ID to fetch appointments for */
  patientId: string | undefined;
  /** Maximum number of appointments to fetch (default: 50) */
  limit?: number;
  /**
   * When true, fetch only upcoming appointments (future-dated, active
   * statuses) instead of the patient's full history. Used by the task
   * detail panel where the operator's question is "what's on this
   * patient's calendar that could conflict with a new booking?" — past
   * completed/cancelled visits are noise in that context.
   *
   * Defaults to false to preserve the patient-detail page behavior,
   * which is the historical record.
   */
  upcoming?: boolean;
  /** Custom class name */
  className?: string;
}

// Status mapping (from smallint to label)
// Statuses: Booked, Confirmed, Completed, Canceled, No Show
const statusConfig: Record<number, { label: string; className: string }> = {
  0: {
    label: 'Booked',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  },
  1: {
    label: 'Booked',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  },
  2: {
    label: 'Booked',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  },
  3: {
    label: 'Confirmed',
    className: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
  },
  4: {
    label: 'Completed',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  },
  5: {
    label: 'Canceled',
    className: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
  },
  6: {
    label: 'No Show',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  },
  7: {
    label: 'Booked',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  },
};

// =============================================================================
// Hook
// =============================================================================

function usePatientAppointments(patientId: string | undefined, limit: number, upcoming: boolean) {
  return useQuery({
    queryKey: upcoming
      ? queryKeys.appointments.upcoming(patientId!, limit)
      : queryKeys.appointments.byPatient(patientId!),
    queryFn: () =>
      upcoming
        ? getAppointmentClient().getUpcoming(patientId!, limit)
        : getAppointmentClient().getByPatient(patientId!, limit),
    enabled: !!patientId,
    // Watch a re-sync land (ADR-284).
    refetchInterval: (query) => syncingRefetchInterval(query.state.data?.appointments ?? []),
  });
}

// =============================================================================
// Component
// =============================================================================

export function PatientAppointmentList({
  patientId,
  limit = 50,
  upcoming = false,
  className,
}: PatientAppointmentListProps): React.ReactElement {
  const { data, isLoading } = usePatientAppointments(patientId, limit, upcoming);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  const appointments: AppointmentWithRelations[] = data?.appointments ?? [];

  // Use TanStack Table for pagination logic only
  const table = useReactTable({
    data: appointments,
    columns: [], // No columns needed for card rendering
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: { pagination },
  });

  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)}>
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    );
  }

  if (appointments.length === 0) {
    return (
      <div className={cn('py-12 text-center', className)}>
        <Calendar className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-gray-500 dark:text-gray-400">No appointments</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
          Appointments will appear here once scheduled or synced from EHR
        </p>
      </div>
    );
  }

  const paginatedRows = table.getRowModel().rows;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Appointment Cards */}
      <div className="space-y-3">
        {paginatedRows.map((row) => {
          const apt = row.original;
          // In the LOCATION's timezone, not the viewer's: a practice can span
          // zones, and the browser's zone moves the clock AND sometimes the day.
          const when = appointmentDateParts(apt.startTime, apt.location?.timezone);
          const statusInfo = statusConfig[apt.status] ?? {
            label: 'Unknown',
            className: 'bg-gray-100 text-gray-700',
          };

          return (
            <div
              key={apt.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedAppointmentId(apt.id)}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedAppointmentId(apt.id)}
              className="flex items-start justify-between rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex flex-col items-center justify-center">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {when.month}
                  </span>
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-300 -mt-0.5">
                    {when.day}
                  </span>
                  <span className="text-[10px] text-blue-500 dark:text-blue-400 -mt-1">
                    {when.year}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <Clock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    {when.time}
                    {/* Only when the location's zone differs from the viewer's,
                        so a single-timezone practice reads exactly as before. */}
                    {when.zoneLabel && (
                      <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                        {when.zoneLabel}
                      </span>
                    )}
                  </div>
                  {apt.provider && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1">
                      <Stethoscope className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                      {apt.provider.firstName} {apt.provider.lastName}
                    </div>
                  )}
                  {apt.isTelehealth ? (
                    <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 mt-1">
                      <Video className="h-3.5 w-3.5" />
                      Telehealth
                    </div>
                  ) : (
                    apt.location && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <MapPin className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                        {apt.location.name}
                      </div>
                    )
                  )}
                  {apt.reasonForVisit && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 italic">
                      {apt.reasonForVisit}
                    </div>
                  )}
                  {/* The EHR's free-text note, kept visually distinct from the
                      reason above: one is why they booked, the other is what
                      the front desk needs to know. */}
                  {apt.notes && (
                    <div className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-gray-400 dark:text-gray-500" />
                      <span className="whitespace-pre-line">{apt.notes}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge variant="secondary" className={cn('text-xs', statusInfo.className)}>
                  {statusInfo.label}
                </Badge>
                <AppointmentEhrSyncStatus appointment={apt} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination - only show if there are multiple pages */}
      {table.getPageCount() > 1 && (
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <AppointmentDetailSheet
        appointmentId={selectedAppointmentId}
        onClose={() => setSelectedAppointmentId(null)}
      />
    </div>
  );
}
